import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Star, Swords, BadgeCheck, ArrowRight, Radio } from "lucide-react";
import { CATEGORY_ICON, CATEGORY_COLOR } from "@/data/marketplaceMockData";

const DIFFICULTY_COLOR = {
  Beginner: "#34D399", Intermediate: "#F59E0B", Advanced: "#FB7185", "All Levels": "#5C9DFF",
};

const Initials = ({ name, color }) => {
  const initials = (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: `${color}22`, color, border: `1px solid ${color}40` }}>
      {initials}
    </div>
  );
};

/** Single, reusable challenge card — renders identically for both real (live-backend)
 * and mock/illustrative marketplace challenges, driven entirely by the shape of `challenge`. */
export const ChallengeCard = ({ challenge, index }) => {
  const c = challenge;
  const accent = CATEGORY_COLOR[c.category] || "#5C9DFF";
  const Icon = CATEGORY_ICON[c.category] || Swords;
  const diffColor = DIFFICULTY_COLOR[c.difficulty] || "#5C9DFF";

  return (
    <motion.div
      data-testid={`challenge-card-${c.id}`}
      initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: (index % 6) * 0.06 }}
      whileHover={{ y: -4 }}
      className="group relative rounded-2xl border border-white/10 bg-[#0A101D]/70 backdrop-blur-xl overflow-hidden hover:border-white/25 transition-colors duration-300"
    >
      {/* cover */}
      <div className="relative h-28 flex items-center justify-center overflow-hidden" style={{ background: `radial-gradient(circle at 30% 20%, ${accent}35, transparent 70%), linear-gradient(135deg, ${accent}18, transparent)` }}>
        <Icon className="w-10 h-10 transition-transform duration-300 group-hover:scale-110" style={{ color: accent }} strokeWidth={1.5} />
        {c.isReal && !c.isCustomReal && (
          <span className="absolute top-3 left-3 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-400/15 border border-emerald-400/30 rounded-full px-2 py-0.5">
            <Radio className="w-2.5 h-2.5" /> Live Platform Track
          </span>
        )}
        {c.isCustomReal && (
          <span className="absolute top-3 left-3 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#B58BFF] bg-[#B58BFF]/15 border border-[#B58BFF]/30 rounded-full px-2 py-0.5">
            <Radio className="w-2.5 h-2.5" /> AI-Generated
          </span>
        )}
        {c.bossBattle && (
          <span className="absolute top-3 right-3 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-violet-300 bg-violet-400/15 border border-violet-400/30 rounded-full px-2 py-0.5">
            <Swords className="w-2.5 h-2.5" /> Boss Battle
          </span>
        )}
      </div>

      <div className="p-5">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{c.category}</span>
        <h3 className="font-heading text-base font-semibold text-white mt-1 leading-snug" data-testid={`challenge-card-title-${c.id}`}>{c.title}</h3>

        <div className="flex items-center gap-1.5 mt-2.5">
          <Initials name={c.creatorName} color={accent} />
          <span className="text-xs text-slate-400 truncate">{c.creatorName}</span>
          {c.creatorVerified && <BadgeCheck className="w-3.5 h-3.5 text-[#5C9DFF] shrink-0" data-testid={`challenge-card-verified-${c.id}`} />}
        </div>

        <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.learners.toLocaleString()}</span>
          <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" /> {c.rating}</span>
          <span>{c.durationDays} Days</span>
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${diffColor}18`, color: diffColor }}>{c.difficulty}</span>
          <span className="text-xs font-bold text-amber-300">+{c.xp.toLocaleString()} XP</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {c.skills.slice(0, 3).map((s) => (
            <span key={s} className="text-[10px] text-slate-300 bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5">{s}</span>
          ))}
        </div>

        <Link
          to={`/marketplace/challenge/${c.id}`} data-testid={`challenge-card-join-${c.id}`}
          className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-xl py-2.5 transition-all hover:-translate-y-0.5"
          style={{ background: accent }}
        >
          View Challenge <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.div>
  );
};
